import { useState, MouseEvent, useEffect } from 'react';
import { FaFacebookF, FaInstagram, FaYoutube } from 'react-icons/fa';
import { MdArrowDropDown, MdArrowDropUp, MdMenu, MdSearch } from 'react-icons/md';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CodNegocio, Roles, RolesProps } from '@dinnisoft/esteio-gestao-common';
import {
  Button,
  Box,
  Drawer,
  Hidden,
  IconButton,
  Typography,
  Toolbar,
  CircularProgress,
  Divider,
  TextField,
  Grid,
} from '@mui/material';
import { get, unregister, unregisterEmailConfirmation } from 'api/api';
import { FazendaCorteIcon, FazendaLeiteIcon } from 'assets/icons/Icons';
import ConfirmationModal from 'components/ConfirmationModal';
import DrawerNavigator from 'components/Drawer';
import ErrorMessage from 'components/ErrorMessage';
import { useAuth } from 'contexts/auth';
import { GlassPopover, UserMenuContainer, UsernameText } from 'styles/Styles';
import { SearchInput } from 'styles/views/Institucional/Header';
import { Footer, Header, FazendaInfoContainer, DeleteModalContainer } from 'styles/views/Layout';
import { handleApiError } from 'utils/handleApiError';

import { FAZENDA, version } from '../../constants';

const DRAWER_WIDTH = 240;

interface Props {
  windowContainer?: () => Window;
  content: JSX.Element;
  title: string;
}

export default function Layout({ windowContainer, content, title }: Props) {
  const context = useAuth();
  const history = useHistory();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElFazenda, setAnchorElFazenda] = useState<null | HTMLElement>(null);
  const [isFetchingFazendaPessoas, setIsFetchingFazendaPessoas] = useState(false);
  const [isFetchingFazendas, setIsFetchingFazendas] = useState(false);
  const [fazendas, setFazendas] = useState<any[]>([]);
  const [filteredFazendas, setFilteredFazendas] = useState<any[]>([]);
  const [fazenda, setFazenda] = useState<any>({});
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');

  const isAbleToSelectFazenda =
    // context.selectedRole === Roles.organizador ||
    // context.selectedRole === Roles.coordenador ||
    context.selectedRole === Roles.central;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePerfilClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePerfilClose = () => {
    setAnchorEl(null);
  };

  const handleFazendaClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElFazenda(event.currentTarget);
  };

  const handleFazendaClose = () => {
    setAnchorElFazenda(null);
    setFilteredFazendas(fazendas);
  };

  const handleFazendaChange = (selected: any) => {
    sessionStorage.setItem(FAZENDA, JSON.stringify(selected));
    setFazenda(selected);
    context.refresh();
    setAnchorElFazenda(null);
  };

  function setSelectedRole(id: string) {
    context.selectRole(parseInt(id));
    handlePerfilClose();
  }

  async function internalLogout() {
    await context.logoutUser();
    handlePerfilClose();
    window.location.hash = ''; // este código é importante para que funcione no desktop
  }

  // const fetchFazendaPessoa = async () => {
  //   setIsFetchingFazendaPessoas(true);

  //   try {
  //     const { data } = await get('fazendaPessoa');
  //     const fazendasPessoas = data.data;
  //     return fazendasPessoas;
  //   } catch (error) {
  //     handleApiError(error);
  //   } finally {
  //     setIsFetchingFazendaPessoas(false);
  //   }
  // };

  const fetchFazendas = async () => {
    setIsFetchingFazendas(true);
    // const dataResult = [];

    try {
      const { data } = await get(`fazendas/getByUsuario/${context?.selectedRole}`);

      setFazendas(data);
      setFilteredFazendas(data);

      // const fazendasPessoas = await fetchFazendaPessoa();

      // const filteredData = fazendas.filter((fazenda: any) => {
      //   return fazendasPessoas.some((fazenda: any) => {
      //     return (
      //       fazendasPessoas.pessoa_id === context?.user?.codPessoa &&
      //       fazenda.id === fazendasPessoas.fazenda_id
      //     );
      //   });
      // });

      // dataResult = filteredData;
    } catch (error) {
      // dataResult = [];
      handleApiError(error);
    } finally {
      setIsFetchingFazendas(false);
    }

    // setFazendas(dataResult);
  };

  const handleDeleteAccountModal = async () => {
    setIsDeletingAccount(true);

    if (!context.user?.email) return;

    try {
      const unregisterResponse = await unregister({
        email: context.user?.email,
      });

      if (unregisterResponse) {
        await unregisterEmailConfirmation({ email: context.user?.email });
        await internalLogout();
        toast.success('Usuário descadastrado com sucesso.');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  function maskEmail(email: string): string {
    const [localPart, domainPart] = email.split('@');

    if (!domainPart) return email; // Se não for um email válido, retorne o original

    const [domainName, ...rest] = domainPart.split('.');
    const domainExtension = rest.join('.');

    const maskedLocal =
      localPart.length > 3
        ? `${localPart.substr(0, 3)}${'*'.repeat(localPart.length - 3)}`
        : localPart;
    const maskedDomain = `${'*'.repeat(domainName.length)}`;

    return `${maskedLocal}@${maskedDomain}.${domainExtension}`;
  }

  useEffect(() => {
    if (isAbleToSelectFazenda) {
      fetchFazendas();

      if (context.selectedFazenda) setFazenda(context.selectedFazenda);
    }
  }, []);

  const container =
    windowContainer !== undefined ? () => windowContainer().document.body : undefined;

  const copyRight = (): string => {
    const dateNow = new Date();
    const year = dateNow.getFullYear();
    return 'Copyright © 2016-' + year;
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value.trim();

    if (value) {
      const fazendasFiltradas = fazendas.filter((fazenda) =>
        fazenda.pessoaJuridica.nomeFantasia.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredFazendas(fazendasFiltradas);
    } else {
      setFilteredFazendas(fazendas);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: 'inherit' }}>
        {/* <CssBaseline /> */}
        <Header
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { sm: `${DRAWER_WIDTH}px` },
          }}
        >
          <Toolbar
            sx={{
              bgcolor: '#0084dc',
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MdMenu />
            </IconButton>

            <Typography color="#fff" sx={{ flexGrow: 1 }} variant="h6" noWrap>
              {title}
            </Typography>

            {isAbleToSelectFazenda && (
              <>
                <Button
                  color="gray"
                  variant="outlined"
                  aria-controls="fazendas-menu"
                  aria-haspopup="true"
                  onClick={handleFazendaClick}
                  disabled={isFetchingFazendas || isFetchingFazendaPessoas}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}
                  startIcon={
                    fazenda?.codNegocio === CodNegocio.leite ? (
                      <FazendaLeiteIcon color="#005daf" size={24} />
                    ) : (
                      fazenda?.codNegocio === CodNegocio.corte && (
                        <FazendaCorteIcon color="#c50000" size={24} />
                      )
                    )
                  }
                >
                  {!isFetchingFazendas || !isFetchingFazendaPessoas ? (
                    !fazenda ? (
                      'Sem fazendas vinculadas'
                    ) : fazenda?.pessoaJuridica?.nomeFantasia ? (
                      <UsernameText>{fazenda?.pessoaJuridica?.nomeFantasia}</UsernameText>
                    ) : (
                      'Selecione uma fazenda'
                    )
                  ) : (
                    <FazendaInfoContainer>
                      <CircularProgress color="inherit" size={24} /> Carregando fazendas...
                    </FazendaInfoContainer>
                  )}
                  {(!isFetchingFazendas || !isFetchingFazendaPessoas) &&
                    (!Boolean(anchorElFazenda) ? (
                      <MdArrowDropDown size={18} />
                    ) : (
                      <MdArrowDropUp size={18} />
                    ))}
                </Button>

                <GlassPopover
                  id="fazendas-menu"
                  anchorEl={anchorElFazenda}
                  open={Boolean(anchorElFazenda)}
                  onClose={handleFazendaClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <UserMenuContainer>
                    <SearchInput
                      id="search-input"
                      placeholder="Pesquisar fazenda"
                      fullWidth
                      startAdornment={<MdSearch size={24} />}
                      sx={{ mb: 1.5, maxWidth: '100% !important' }}
                      onChange={handleSearch}
                    />
                    {!isFetchingFazendas || !isFetchingFazendaPessoas ? (
                      filteredFazendas.length > 0 ? (
                        filteredFazendas.map((item: any, index) => (
                          <Button
                            // disabled={fazenda.id === item.id}
                            key={index}
                            onClick={() => handleFazendaChange(item)}
                            fullWidth
                            startIcon={
                              item.codNegocio === 1 ? (
                                <FazendaLeiteIcon color="#005daf" size={24} />
                              ) : (
                                <FazendaCorteIcon color="#c50000" size={24} />
                              )
                            }
                          >
                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                              <Typography color="rgba(0,0,0,0.87)" fontSize={14}>
                                {item?.pessoaJuridica?.nomeFantasia}
                              </Typography>
                              <Typography color="grey" fontSize={10}>
                                {item?.nomeProdutor}
                              </Typography>
                            </Box>
                          </Button>
                        ))
                      ) : (
                        <FazendaInfoContainer>Fazenda não encontrada.</FazendaInfoContainer>
                      )
                    ) : (
                      <FazendaInfoContainer>
                        <CircularProgress color="inherit" size={24} /> Carregando fazendas...
                      </FazendaInfoContainer>
                    )}
                  </UserMenuContainer>
                </GlassPopover>
              </>
            )}

            <Button
              color="gray"
              variant="outlined"
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handlePerfilClick}
              sx={{ ml: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }}
            >
              <UsernameText>{context?.user?.pessoa?.pessoaFisica?.nome}</UsernameText>
              {!Boolean(anchorEl) ? <MdArrowDropDown size={18} /> : <MdArrowDropUp size={18} />}
            </Button>
            <GlassPopover
              id="simple-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handlePerfilClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <UserMenuContainer>
                {context.roles.map((item: RolesProps) => {
                  if (context.selectedRole !== Roles.admin) {
                    if (context.selectedRole >= 0 && context.selectedRole === item.id) {
                      return (
                        <Button fullWidth key={item.id} disabled onClick={handlePerfilClose}>
                          {item.name}
                        </Button>
                      );
                    } else {
                      return (
                        <Button
                          fullWidth
                          key={item.id}
                          onClick={() => setSelectedRole(item.id.toString())}
                        >
                          {item.name}
                        </Button>
                      );
                    }
                  }
                })}
                {context.selectedRole !== Roles.admin && (
                  <>
                    <Button fullWidth onClick={() => history.push('/profile')}>
                      Alterar dados
                    </Button>

                    <Button
                      color="error"
                      fullWidth
                      onClick={() => setIsDeleteAccountModalOpen(true)}
                    >
                      Excluir conta
                    </Button>
                  </>
                )}

                <Divider />
                <Button fullWidth color="error" onClick={() => internalLogout()}>
                  Sair
                </Button>
              </UserMenuContainer>
            </GlassPopover>
          </Toolbar>
        </Header>

        <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
          <Hidden smUp implementation="css">
            <Drawer
              container={container}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true,
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: DRAWER_WIDTH,
                  bgcolor: '#222d32',
                },
              }}
            >
              {DrawerNavigator({ title })}
            </Drawer>
          </Hidden>
          <Hidden xsDown implementation="css">
            <Drawer
              sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: DRAWER_WIDTH,
                  bgcolor: '#222d32',
                },
              }}
              variant="permanent"
              open
            >
              {DrawerNavigator({ title })}
            </Drawer>
          </Hidden>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          }}
        >
          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Toolbar />
            {content}
          </Box>

          <Footer>
            <Grid container>
              <Grid item xs={4}>
                <Box display="flex">
                  <Typography color="#444" fontWeight={700} fontSize="14px">
                    {copyRight()}
                  </Typography>
                  <Typography color="#444" fontSize="14px">
                    &nbsp;Todos direitos reservados.
                  </Typography>
                </Box>
                <Box display="flex" gap="10px" mt="5px">
                  <Link
                    target={'_blank'}
                    to={'./' + context.selectedProject + '/files/TERMOS-DE-USO.pdf'}
                    rel="noreferrer"
                  >
                    <Typography
                      fontSize="12px"
                      color="#424040"
                      sx={{
                        '&:hover': {
                          fontWeight: 700,
                        },
                      }}
                    >
                      Termos
                    </Typography>
                  </Link>
                  <Link
                    target={'_blank'}
                    to={'./' + context.selectedProject + '/files/POLITICA-DE-PRIVACIDADE.html'}
                    rel="noreferrer"
                  >
                    <Typography
                      fontSize="12px"
                      color="#424040"
                      sx={{
                        '&:hover': {
                          fontWeight: 700,
                        },
                      }}
                    >
                      Privacidade
                    </Typography>
                  </Link>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Grid container width="100%" height="100%">
                  {/* Instagram */}
                  <Grid
                    item
                    xs
                    component={Link}
                    to="https://www.instagram.com/esteiogestao/"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    <Box
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FaInstagram color="#ac2bac" size="2em" />
                    </Box>
                  </Grid>
                  {/* Facebook */}
                  <Grid
                    item
                    xs
                    component={Link}
                    to="https://www.facebook.com/EsteioGestaoAgropecuaria"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    <Box
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FaFacebookF color="#3b5998" size="2em" />
                    </Box>
                  </Grid>
                  {/* Youtube */}
                  <Grid
                    item
                    xs
                    component={Link}
                    to="https://www.youtube.com/@esteiogestao"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    <Box
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FaYoutube color="#ed302f" size="2em" />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid component={Box} item xs={2} display="flex">
                <Typography color="#444" fontWeight={700} fontSize="14px">
                  Versão Web
                </Typography>
                <Typography color="#444" fontSize="14px">
                  &nbsp;{version} {process.env.NODE_ENV === 'development' ? '(dev)' : ''}
                </Typography>
              </Grid>
            </Grid>
          </Footer>
        </Box>
      </Box>

      {isDeleteAccountModalOpen && (
        <ConfirmationModal
          open={isDeleteAccountModalOpen}
          onClose={() => setIsDeleteAccountModalOpen(false)}
          onConfirm={handleDeleteAccountModal}
          buttonDisabled={
            confirmationEmail.length !== context.user?.email.length || isDeletingAccount
          }
          isLoading={isDeletingAccount}
        >
          <DeleteModalContainer>
            <p>Favor confirmar seu email para efetuar a exclusão de sua conta.</p>

            <TextField
              label="Email"
              placeholder={context.user?.email && maskEmail(context.user?.email)}
              onChange={(e) => setConfirmationEmail(e.target.value)}
              value={confirmationEmail}
              error={
                confirmationEmail.length === context.user?.email.length &&
                confirmationEmail !== context.user?.email
              }
            />

            {confirmationEmail.length === context.user?.email.length &&
              confirmationEmail !== context.user?.email && <ErrorMessage error="Email incorreto" />}
          </DeleteModalContainer>
        </ConfirmationModal>
      )}
    </>
  );
}
